<template>
    <div class="certificate-content">
      <div class="header-actions">
        <div class="search-container">
          <input type="text" placeholder="Search certificates by name " v-model="searchQuery" class="search-input">
        </div>
  
        <button class="create-plan-btn" @click="() => { showModal = true }">
          <img src="@/assets/images/add-border-icon.svg" class="btn-icon" alt="Add" />
          CREATE CERTIFICATE PLAN
        </button>
  
      </div>
  
      <div class="table-container">
        <SkeletonTableLoader v-if="loading" :rowCount="7" :columnCount="6" />
  
        <div v-else-if="!loading && filteredCertificates.length === 0" class="no-data-container">
          <p class="no-data-text">No certificates found. Try adjusting your search or creating a new certificate.</p>
        </div>
  
  
        <table v-else-if="!loading && filteredCertificates.length > 0">
          <thead>
            <tr>
              <th>ID</th>
              <th>Certificate Name</th>
              <th>Created At</th>
              <th>Updated At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(certificate, index) in filteredCertificates" :key="certificate.id">
              <td>{{ certificate.id }}</td>
              <td>{{ certificate.title }}</td>
              <td>{{ formatDateTime(certificate.createdAt) }}</td>
              <td>{{ formatDateTime(certificate.updatedAt) }}</td>
              <td>
                <div class="actions-dropdown">
                  <button class="actions-btn" @click="toggleActionMenu(certificate.id)">
                    ACTIONS
                    <i class="fas fa-chevron-down" />
                  </button>
                  <div v-if="activeActionMenu === certificate.id" class="custom-dropdown-menu">
                    <button class="action-option" @click="editCertificate(certificate)">
                      <i class="fas fa-edit" />
                      Edit
                    </button>
                    <button class="action-option text-red-500" @click="deleteSelectedCertificate(certificate)">
                      <i class="fas fa-trash" />
                      Delete
                    </button>
                    <button class="action-option" @click="designCertificate(certificate)">
                      <i class="fas fa-paint-brush" />
                      Design Template
                    </button>
                    <button
                      class="action-option" @click="issueCertificates(certificate)">
                      <i class="fas fa-award" />
                      Issue Certificates
                    </button>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <ManageCertificateModal
       :visible="showModal"
       @updateModalState="toggleManageModal"
       :certificateData="selectedCertificateData"
       :isEditMode="isEditing"
       :initialTemplateMode="initialTemplateMode" />

      <IssueCertificatesModal 
      :visible="showUploadModal"
      :certificate="selectedCertificateData"
      @updateModalState="showUploadModal = $event"
      />
    </div>
  </template>
  
  <script>
  import ManageCertificateModal from './ManageCertificateModal.vue'
  import IssueCertificatesModal  from '../Certificate/IssueCertificatesModal.vue'
  import SkeletonTableLoader from '@/components/Shared/SkeletonTableLoader.vue'
  import { mapActions, mapState } from 'vuex'
  import { formatToDDMMMYY_HHmm } from "../../../helpers/FormatHelpers";
  
  export default {
    name: 'TemplateCertificates',
    components: { ManageCertificateModal, IssueCertificatesModal, SkeletonTableLoader },
    data() {
      return {
        searchQuery: '',
        showModal: false,
        showUploadModal: false,
        loading: false,
        activeActionMenu: null,
        isEditing: false,
        selectedCertificateData: null,
        initialTemplateMode: null,
      }
    },
    computed: {
      ...mapState("certificate", ["certificates"]),
  
      certificateList(){
       return Object.values(this.certificates || {});
      },
  
      filteredCertificates() {
        return this.certificateList.filter(certificate =>
          certificate.title.toLowerCase().includes(this.searchQuery.toLowerCase())
        )
      }
    },
    methods: {
      ...mapActions("certificate", ["fetchCertificates", "deleteCertificate"]),
  
      toggleManageModal(state) {
        if (state !== undefined) {
          this.showModal = state;
        } else {
          this.showModal = !this.showModal;
        }
  
        if (!state) {
          this.selectedCertificateData = null;
          this.isEditing = false;
          this.initialTemplateMode = null;
        }
      },
  
      async getCertificates() {
        try {
          this.loading = true;
          await this.fetchCertificates();
        }
        catch (e) {
          this.$toasted.error("Unable to load certificates.", {
            icon: "exclamation-triangle",
            theme: "outline",
            position: "bottom-center",
            singleton: true,
            duration: 3000
          });
        }
        finally { this.loading = false; }
  
      },
      toggleActionMenu(id) {
        this.activeActionMenu = this.activeActionMenu === id ? null : id
      },
      editCertificate(certificate) {
        this.selectedCertificateData = certificate;
        this.isEditing = true;
        this.initialTemplateMode = null;
        this.showModal = true;
      },
      designCertificate(certificate) {
        this.selectedCertificateData = certificate;
        this.isEditing = true;
        this.initialTemplateMode = 'design';
        this.showModal = true;
        this.activeActionMenu = null;
      },
      issueCertificates(certificate) {
        this.selectedCertificateData = certificate;
        this.showUploadModal = true;
      },
      async deleteSelectedCertificate(certificate) {
        if (!certificate?.id) return;

        const confirmed = await this.$bvModal.msgBoxConfirm(
          `Are you sure you want to delete the certificate "${certificate.title}"?`,
          {
            title: "Confirm Deletion",
            okTitle: "Yes, Delete",
            cancelTitle: "Cancel",
            centered: true,
            size: "md",
            okVariant: "danger"
          }
        );

        if (!confirmed) return;

        try {
          await this.deleteCertificate(certificate.id);
          this.$toasted.success("Certificate deleted successfully.", {
            icon: "check-circle",
            theme: "outline",
            position: "bottom-center",
            singleton: true,
            duration: 3000
          });

        } catch (error) {
          this.$toasted.error("Failed to delete certificate. Please try again.", {
            icon: "exclamation-triangle",
            theme: "outline",
            position: "bottom-center",
            singleton: true,
            duration: 3000
          });
        }
      },
      formatDateTime(date) {
        if(!date) return "N/A";
        return formatToDDMMMYY_HHmm(date);
      }
    },
    async mounted() {
      this.getCertificates()
    },
  }
  </script>
  
  <style scoped>
  .certificate-content {
    height: calc(100svh - var(--header-height) - 65px);
    padding: 1.5rem;
    overflow: auto;
  }
  
  .header-actions {
      display: flex;
      margin-bottom: 1.5rem;
      justify-content: space-between;
  }
  
  .search-container {
    flex: 1;
    margin-right: 1.5rem;
  }
  
  .search-input {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    font-size: 0.875rem;
  }
  
  .create-plan-btn {
    background-color: #fff;
    border: 1px solid var(--primary-color);
    border-radius: 0.375rem;
    color: #000;
    font-size: 0.875rem;
    font-weight: 500;
    padding: 0.5rem 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    transition: all 0.2s;
  }
    
  .create-plan-btn:hover {
    background-color: #f9fafb;
  }
    
  .btn-icon {
    width: 13px;
    height: 13px;
    margin-left: 0;
  }
  .table-container {
    margin-bottom: 1.5rem;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
  }
  
  table {
    width: 100%;
    border-collapse: collapse;
  }
  
  th,
  td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid #e5e7eb;
  }
  
  th {
    background-color: #f9fafb;
    font-weight: 500;
    color: #374151;
  }
  .actions-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    background-color: white;
    font-size: 0.875rem;
    cursor: pointer;
  }
  
  .actions-dropdown {
    position: relative;
  }
  
  .custom-dropdown-menu {
    position: absolute;
    left: 0;
    top: 100%;
    margin-top: 0.25rem;
    background-color: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    z-index: 999;
    min-width: 120px;
    overflow: visible;
  }
  
  .action-option {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    color: #374151;
    width: 100%;
    border: none;
    background: none;
    text-align: left;
    cursor: pointer;
  }
  
  .action-option:hover {
    background-color: #f9fafb;
  }
  
  .text-red-500 {
    color: #ef4444;
  }
  
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
    color: #555;
  }
  
  .spinner {
    width: 50px;
    height: 50px;
    border: 5px solid rgba(0, 0, 0, 0.1);
    border-top-color: black;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  .loading-text {
    margin-top: 10px;
    font-size: 16px;
    font-weight: 500;
  }
  
  .no-data-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
    color: #333;
    text-align: center;
    margin-top: 10px;
  }
  
  .no-data-icon {
    font-size: 40px;
    margin-bottom: 10px;
  }
  
  .no-data-text {
    font-size: 16px;
    font-weight: 500;
  }
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  
  </style>